import { useEffect } from "react";

import packageJson from "../../package.json";
import type { WatchedSecondsSlideData } from "../../types/telemetry";
import { captionLanguage, playback, playerWatched, quality } from "../apollo/typePolicies/LocalStorage";
import { setIsFullscreen } from "../components/Video/useVideo";
import { stateVar } from "../hooks/useAppState";
import { videoProgressVar } from "../hooks/useVideoProgress";
import { formatTelemetryActionMetaValue } from "../util/captionLanguages";
import { playerModality } from "../util/constants";
import { initializeSessionId } from "../util/sessionId";
import { store } from "./TelemetryDataAccess";
import type { Telemetry } from "./TelemetryModel";

export interface TelemetryPayloadOptions {
  feature: string;
  action: string;
  action_meta?: string;
  [key: string]: string | number | boolean | null | undefined;
}

/**
 * Creates a telemetry payload object that can be used for TelemetryModel instantiation
 * or stored in local storage for later use
 */
export const createTelemetryPayload = (options: TelemetryPayloadOptions): Telemetry | null => {
  try {
    const { feature, action, action_meta = "", ...additionalProps } = options;

    // Get current app state
    const appState = stateVar();
    const courseId = appState?.courseId;
    const moduleId = appState?.moduleId;
    const userId = appState?.instance?.userId;

    // Ensure required data is available
    const dataLoaded: boolean = !!courseId && !!moduleId && !!userId;
    if (!dataLoaded || typeof action !== "string" || typeof feature !== "string") {
      return null;
    }

    // Get current values from local storage & reactive variables
    const networkSpeedData = localStorage.getItem("NETWORK_SPEED");
    const networkSpeed = networkSpeedData ? JSON.parse(networkSpeedData) : {};
    const currentPlaybackSpeed = playback();
    const currentVideoResolution = quality();
    const currentCaptionLanguage = captionLanguage();
    const currentSlideId = appState?.slideId;

    // Get persisted settings
    const volumeData = localStorage.getItem("PLAYER_VOLUME");
    const autoplayData = localStorage.getItem("PLAYER_AUTOPLAY");
    const mutedData = localStorage.getItem("MUTED");
    const volume = volumeData ? parseInt(volumeData, 10) : 50;
    const autoplay = autoplayData === "true";
    const muted = mutedData === "true";

    // Get video progress
    const videoProgressData = videoProgressVar();
    const mediaTime = Math.floor(videoProgressData.playedSeconds);

    // Get fullscreen state
    const isFullscreen = setIsFullscreen();

    return {
      feature,
      action,
      action_meta: typeof action_meta === "string" ? action_meta : "",
      course_id: courseId as number,
      lo_uuid: moduleId as string,
      slide_uuid: currentSlideId ?? undefined,
      video_resolution: currentVideoResolution,
      autoplay,
      volume_percent: volume,
      playback_speed: currentPlaybackSpeed,
      student_session: initializeSessionId(userId as number),
      player_modality: playerModality,
      player_version: packageJson.version,
      student_id: userId as number,
      mute: muted,
      network_speed_mbps: networkSpeed.mbps,
      media_time: mediaTime,
      fullscreen: isFullscreen,
      closed_captions: formatTelemetryActionMetaValue(currentCaptionLanguage),
      ...additionalProps, // Additional props to override defaults
    };
  } catch (error) {
    console.error("Failed to create telemetry payload:", error, { options });
    return null;
  }
};

/**
 * Send telemetry data
 */
const sendSlideTelemetry = async (slideUuid: string, slideData: WatchedSecondsSlideData) => {
  try {
    await store(slideData.payload);
  } catch (error) {
    console.error("Failed to send telemetry for slide:", slideUuid, error);
  }
};

/**
 * Default pathname getter function - stable reference to avoid unnecessary effect re-runs
 */
const defaultGetPathname = () => window.location.pathname;

/**
 * Checks routinely for telemetry data waiting to be sent and if a suitable time, sends it
 */
export function TelemetryAwaitingEmitter({
  getPathname = defaultGetPathname,
}: {
  getPathname?: () => string;
} = {}): null {
  useEffect(() => {
    const interval = setInterval(() => {
      try {
        // Paths where we should not emit telemetry (e.g., during auth flows)
        const blockedPaths = ["/authorize"];
        const currentPath = getPathname();
        if (blockedPaths.includes(currentPath)) {
          return;
        }

        const appState = stateVar();
        const currentSlideId = appState?.slideId;
        const watchedData = playerWatched();
        const slidesToRemove: string[] = [];

        Object.keys(watchedData).forEach(slideUuid => {
          const slideData = watchedData[slideUuid];

          if (slideUuid !== currentSlideId) {
            sendSlideTelemetry(slideUuid, slideData);
            slidesToRemove.push(slideUuid);
          }
        });

        // Clean up the local storage data for items now sent
        if (slidesToRemove.length > 0) {
          const updatedWatchedData = { ...watchedData };
          slidesToRemove.forEach(slideUuid => {
            delete updatedWatchedData[slideUuid];
          });
          playerWatched(updatedWatchedData);
        }
      } catch (error) {
        console.error("Error in TelemetryAwaitingEmitter:", error);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [getPathname]);

  return null;
}
